import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { staffService } from '../services/api';
import { useApp } from '../context/AppContext';

// Flip to true to require setting a password when creating a staff
// account (e.g. if password login gets turned back on in Login.js).
// Accounts created while this is false get a random unknown password —
// use "New password" on Edit to set a real one later if needed.
const SHOW_PASSWORD_FIELD = false;

const Staff = () => {
  const { currentUser, notify, startImpersonation } = useApp();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('teacher');
  const [teachingLanguage, setTeachingLanguage] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: 'teacher', password: '', teachingLanguage: [] });

  const toggleLanguageList = (list, lang) => (list.includes(lang) ? list.filter((l) => l !== lang) : [...list, lang]);

  const loadStaff = useCallback(
    () => staffService.list().then(setStaff).catch((err) => notify(err.message, 'error')),
    [notify]
  );

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await staffService.create(name, email, password, role, role === 'teacher' ? teachingLanguage : []);
      setName('');
      setEmail('');
      setPassword('');
      setRole('teacher');
      setTeachingLanguage([]);
      notify('Staff account created', 'success');
      loadStaff();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditForm({ name: s.name, email: s.email || '', role: s.role, password: '', teachingLanguage: s.teachingLanguage || [] });
  };

  const cancelEdit = () => setEditingId(null);

  const handleSaveEdit = async (e, id) => {
    e.preventDefault();
    try {
      const updates = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        teachingLanguage: editForm.role === 'teacher' ? editForm.teachingLanguage : null,
      };
      if (editForm.password) updates.password = editForm.password;
      await staffService.update(id, updates);
      notify('Staff account updated', 'success');
      setEditingId(null);
      loadStaff();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleToggleActive = async (s) => {
    try {
      await staffService.update(s.id, { isActive: !s.isActive });
      notify(s.isActive ? 'Account deactivated' : 'Account reactivated', 'success');
      loadStaff();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleDelete = async (s) => {
    const confirmed = window.confirm(`Delete ${s.name}'s account? Any batches assigned to them become unassigned.`);
    if (!confirmed) return;
    try {
      await staffService.remove(s.id);
      notify('Staff account deleted', 'success');
      loadStaff();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleViewAs = (s) => {
    startImpersonation(s, s.role);
    navigate('/');
  };

  return (
    <div className="page">
      <h1>Staff</h1>

      <div className="card">
        <h2>Create a staff account</h2>
        <form className="inline-form" onSubmit={handleCreate}>
          <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
          <input
            type="email"
            placeholder="Email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {SHOW_PASSWORD_FIELD && (
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="teacher">Teacher</option>
            {currentUser.isSuperAdmin && <option value="admin">Admin</option>}
          </select>
          {role === 'teacher' && (
            <div className="language-checkboxes">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={teachingLanguage.includes('Tamil')}
                  onChange={() => setTeachingLanguage(toggleLanguageList(teachingLanguage, 'Tamil'))}
                />
                Tamil
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={teachingLanguage.includes('English')}
                  onChange={() => setTeachingLanguage(toggleLanguageList(teachingLanguage, 'English'))}
                />
                English
              </label>
            </div>
          )}
          <button type="submit" className="btn-primary">Create</button>
        </form>
      </div>

      <div className="card">
        <h2>All staff</h2>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Language</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) =>
                editingId === s.id ? (
                  <tr key={s.id}>
                    <td colSpan={6}>
                      <form className="inline-form" onSubmit={(e) => handleSaveEdit(e, s.id)}>
                        <input
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          placeholder="Name"
                          required
                        />
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          placeholder="Email (optional)"
                        />
                        <select
                          value={editForm.role}
                          onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                        >
                          <option value="teacher">Teacher</option>
                          {(currentUser.isSuperAdmin || s.role === 'admin') && (
                            <option value="admin">Admin</option>
                          )}
                        </select>
                        {editForm.role === 'teacher' && (
                          <div className="language-checkboxes">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={editForm.teachingLanguage.includes('Tamil')}
                                onChange={() =>
                                  setEditForm({
                                    ...editForm,
                                    teachingLanguage: toggleLanguageList(editForm.teachingLanguage, 'Tamil'),
                                  })
                                }
                              />
                              Tamil
                            </label>
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={editForm.teachingLanguage.includes('English')}
                                onChange={() =>
                                  setEditForm({
                                    ...editForm,
                                    teachingLanguage: toggleLanguageList(editForm.teachingLanguage, 'English'),
                                  })
                                }
                              />
                              English
                            </label>
                          </div>
                        )}
                        {SHOW_PASSWORD_FIELD && (
                          <input
                            type="password"
                            value={editForm.password}
                            onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                            placeholder="New password (optional)"
                          />
                        )}
                        <button type="submit" className="btn-primary">Save</button>
                        <button type="button" className="btn-link" onClick={cancelEdit}>Cancel</button>
                      </form>
                    </td>
                  </tr>
                ) : (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{s.email || <span className="text-muted">No email</span>}</td>
                    <td>
                      <span className="badge">{s.isSuperAdmin ? 'Super Admin' : s.role}</span>
                    </td>
                    <td>{s.role === 'teacher' ? (s.teachingLanguage?.length ? s.teachingLanguage.join(', ') : <span className="text-muted">—</span>) : '—'}</td>
                    <td>
                      {s.email ? (
                        <span className={`badge ${s.isActive ? 'badge-active' : 'badge-ended'}`}>
                          {s.isActive ? 'Active' : 'Inactive'}
                        </span>
                      ) : (
                        <span className="badge">No login</span>
                      )}
                    </td>
                    <td>
                      <div className="btn-group">
                        {currentUser.isSuperAdmin && s.isActive && !s.isSuperAdmin && (
                          <button className="btn-secondary" onClick={() => handleViewAs(s)} title={`View the portal as this ${s.role}`}>
                            View as
                          </button>
                        )}
                        {(!s.isSuperAdmin || s.id === currentUser.id) && (
                          <button className="btn-secondary" onClick={() => startEdit(s)}>Edit</button>
                        )}
                        {s.email && (!s.isSuperAdmin || s.id === currentUser.id) && (
                          <button className="btn-secondary" onClick={() => handleToggleActive(s)}>
                            {s.isActive ? 'Deactivate' : 'Reactivate'}
                          </button>
                        )}
                        {s.id !== currentUser.id && !s.isSuperAdmin && (
                          <button className="btn-secondary btn-danger" onClick={() => handleDelete(s)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Staff;
